import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary specifically for Widget SDK Provider
 * Prevents SDK errors from crashing the entire application
 */
export class WidgetSdkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console for debugging
    console.error('[WidgetSdkErrorBoundary] Widget SDK error caught:', error, errorInfo);
    
    // Handle "Script error" (CORS errors) gracefully
    if (error.message === 'Script error.' || error.message === '') {
      console.warn('[WidgetSdkErrorBoundary] CORS script error detected - this is expected for cross-origin scripts');
    }
    
    // You could also log to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI instead of crashing
      return (
        <Box
          sx={{
            padding: 3,
            textAlign: 'center',
            bgcolor: 'warning.light',
            borderRadius: 2,
            border: 1,
            borderColor: 'warning.main',
            margin: 2,
          }}
        >
          <Typography variant="h6" color="warning.dark" gutterBottom>
            Widget SDK Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The widget SDK encountered an error and could not be initialized.
            The rest of the application continues to function normally.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Error: {this.state.error?.message || 'Unknown error'}
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

